import { createSelector } from 'reselect'
import {
  getAlgoliaDataHitsPerPage,
  getAlgoliaDataPaginationCurrent,
  getAlgoliaDataHitsResults,
  getAlgoliaDataHitsTotal,
  getAlgoliaDataHits,
} from 'selectors/algolia2/data'
import { getPage } from 'selectors/algolia2/url'
import { getSelectedFacetNameFirstAttribute } from 'selectors/algolia2/facets'
import { getPageData, getPageParams, getPagePath } from 'selectors/page'
import { slugify } from 'lib/slugify'
import { splitAndCapitalizeString, urlQuery } from 'lib/helpers'
import { getFirstThreeArtists } from '../helpers'
import { getLocation } from 'selectors/shared/location'
export { getPageData, getPageParams, getPagePath }
export { getAlgoliaUserToken } from 'selectors/user/cookie'

export const getHitsIsSeoValidCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number => results.reduce((count, { isSeoValid }) => (count += isSeoValid ? 1 : 0), 0)
)
export const getFeatureHitsPerPage = createSelector([getPageParams], ({ hitsPerPage }): number => {
  return Number(hitsPerPage) || 50
})
export const getHitsAvailableForSaleCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number =>
    results.reduce((count, { originalStatus }) => (count += originalStatus === 'avail' ? 1 : 0), 0)
)
export const getCategory = createSelector(
  getSelectedFacetNameFirstAttribute('category'),
  (category): string => category || ''
)
export const getKeyword = createSelector(
  [getPageParams],
  (pageParams): string => pageParams.keyword || ''
)
export const getUrlPathString = createSelector(
  [getCategory, getKeyword],
  (category, keyword): string => `/${slugify(category)}/${slugify(keyword)}/feature`
)
export const getAlgoliaQuery = createSelector(
  [getAlgoliaDataHitsPerPage, getAlgoliaDataPaginationCurrent],
  (hitsPerPage, page): Record<string, any> => ({
    ...(hitsPerPage !== 50
      ? {
          hitsPerPage,
        }
      : {}),
    ...(page !== 0
      ? {
          page: page + 1,
        }
      : {}),
  })
)
export const getUrlQueryString = createSelector([getAlgoliaQuery], (algoliaQueryParams): string =>
  urlQuery(algoliaQueryParams)
)
export const getUrlString = createSelector(
  [getUrlPathString, getUrlQueryString],
  (urlPathString, urlQueryString): string => `${urlPathString}${urlQueryString}`
)
export const getDataLayer = createSelector(
  [getCategory, getKeyword, getPage, getKeyword],
  (category, keyword, pagination, sort): Record<string, any> => ({
    category,
    event: 'pagetype',
    keyword,
    pagetype: 'browse',
    pagination,
    subpagetype: 'feature',
  })
)
export const getTitle = createSelector(
  [getKeyword],
  (keyword): string => `${splitAndCapitalizeString(keyword)}`
)
export const getSeoCategory = createSelector([getCategory], (category): string => {
  const cleanCategory =
    category === ('Paintings' || 'All')
      ? category.replace(/^[All]$/gi, 'Artworks')
      : `${category} Artworks`
  return cleanCategory || ''
})
export const getDescription = createSelector(
  [getSeoCategory, getTitle, getAlgoliaDataHitsTotal, getAlgoliaDataHits],
  (category, title, totalHits, algoliaHits): string => {
    const { results } = algoliaHits
    const artists = getFirstThreeArtists(results)
    return `Results for "${title}" ${category}. Discover ${totalHits} ${category} by top emerging artists from around the world, including ${artists[0]}, ${artists[1]}, and ${artists[2]}. Buy original art worry free with our 14-day satisfaction guarantee.`
  }
)

export const getIsIndexAllowedForSEOPurposes = createSelector(
  [getLocation],
  (location): boolean => {
    // Specific paths that should not be indexed when they have page parameters
    const NOINDEX_PAGINATED_PATHS = [
      'saatchiart.com/paintings/facve/feature',
      'saatchiart.com/paintings/family-nudism/feature',
      'saatchiart.com/paintings/artest/feature'
    ]
    // Check if current URL matches one of the specific paths that should not be indexed when paginated
    const isSpecificPath = NOINDEX_PAGINATED_PATHS.some(noindexPath =>
      location.includes(noindexPath)
    )

    // Check if page parameter exists in the URL
    const hasPageParam = location.includes('page=')

    // If it's a specific path AND has page parameter, don't index
    if (isSpecificPath && hasPageParam) {
      return false
    }

    // Otherwise allow indexing
    return true
  }
)

export const getMetaData = createSelector(
  [
    getCategory,
    getDescription,
    getTitle,
    getHitsIsSeoValidCount,
    getHitsAvailableForSaleCount,
    getAlgoliaDataHitsTotal,
    getIsIndexAllowedForSEOPurposes
  ],
  (
    category,
    description,
    metaTitle,
    isSeoValidCount,
    availableForSaleCount,
    hitsTotal,
    isIndexAllowedForSEOPurposes,
  ): Record<string, any> => {
    const title = `${metaTitle} ${category} | Saatchi Art`
    const noFollow = hitsTotal < 10 || isSeoValidCount < 10 || availableForSaleCount < 1
    return {
      description,
      title,
      og: {
        description,
        title,
        type: 'product',
      },
      preloadImages: [],
      robots: isIndexAllowedForSEOPurposes && !noFollow,
      schema: {
        webpage: {
          headline: title,
          description,
        },
      },
    }
  }
)
